import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Box, TableRow } from '@material-ui/core';
import { DEFAULT_TABLE_ROW_HEIGHT, LoaderRow, TableCell, Typography } from '../../../../ui-kit';
import { RossumUploadStatus } from '../../../../generated/graphql';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { PendingDeliveryLists } from './types';
import { pendingDeliveryListsHeadCells } from './constants';
import ReviewStatusIndicator from './ReviewStatusIndicator';
import ActionButtons from './ActionButtons';

interface PendingDeliveryListsTableContentProps {
    pendingDeliveryLists: PendingDeliveryLists;
    isLoading: boolean;
    onDelete: (rossumUploadId: string, filename: string) => void;
}

const PendingDeliveryListsTableContent = ({
    pendingDeliveryLists,
    isLoading,
    onDelete,
}: PendingDeliveryListsTableContentProps) => {
    const { user: { cultureCode } } = useUser();
    const { dateTimeService } = useDateTimeService();
    const match = useRouteMatch();
    const history = useHistory();
    const referrer = history.location.pathname;

    if (isLoading) {
        return (
            <LoaderRow columnCount={pendingDeliveryListsHeadCells.length} itemsPerPage={10000000} itemHeight={DEFAULT_TABLE_ROW_HEIGHT} />
        );
    }

    const rows = pendingDeliveryLists.map(row => (
        <TableRow tabIndex={-1} key={row.id}>
            {pendingDeliveryListsHeadCells.map((cell, index) => {
                const isFinished = row.status === RossumUploadStatus.Confirmed;

                let cellValue: React.ReactNode;
                if (cell.id === 'actions') {
                    const verifyUrl = `${match.url}/verify/${row.id}`;

                    cellValue = (
                        <ActionButtons
                            isFinished={isFinished}
                            onDelete={() => onDelete(row.id, row.filename)}
                            proceedLink={{ pathname: verifyUrl, state: { referrer } }}
                        />
                    );
                } else if (cell.id ==='status') {
                    cellValue = <ReviewStatusIndicator status={row[cell.id] as RossumUploadStatus} />;
                } else if (cell.id === 'createdAtUtc') {
                    cellValue = (
                        <Box pl={1}>
                            <Typography variant='body2' color={isFinished ? 'textPrimary' : 'textSecondary'}>
                                {dateTimeService.localizeDate(row[cell.id], cultureCode)}
                            </Typography>
                        </Box>
                    );
                } else {
                    cellValue = (
                        <Typography variant='body2' color={isFinished ? 'textPrimary' : 'textSecondary'}>
                            {row[cell.id]}
                        </Typography>
                    );
                }

                return (
                    <TableCell
                        align={cell.align}
                        isSticky={index === 0}
                        key={`${cell.id} - ${row.id}`}
                        style={{ padding: 8, zIndex: index === 0 ? 1 : 'initial' }}
                    >
                        {cellValue}
                    </TableCell>
                );
            })}
        </TableRow>
    ));

    return (
        <>
            {rows}
        </>
    );
};

export default PendingDeliveryListsTableContent;
